import React from 'react';
import { Grid, Paper, Typography, Card, CardHeader, CardContent, Chip, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import GavelIcon from '@mui/icons-material/Gavel';
import DashboardSelector from './DashboardSelector';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  minHeight: '100vh',
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
}));

const DataTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '0.5rem 1rem',
    height: '32px',
    fontSize: '0.875rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#064d51',
    color: '#ffffff',
    fontWeight: 600,
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: 'rgba(192, 242, 243, 0.1)',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: '#064d51',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const TrendIndicator = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === 'up' ? '#27ae60' : '#e74c3c',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

// API integration function - Replace with actual API call in production
const fetchLegalDashboardData = async () => {
  // In production, this would be an API call to fetch real data
  // Example: return await axios.get('/api/dashboard/legal', { headers: { Authorization: `Bearer ${token}` } });
  
  // For now, return sample data
  return {
    // Case Management
    activeCases: 8,
    casesByStatus: [
      { status: 'New', count: 3, color: '#3498db' },
      { status: 'In Progress', count: 4, color: '#f39c12' },
      { status: 'Pending Client Input', count: 1, color: '#e74c3c' },
      { status: 'Resolved', count: 12, color: '#27ae60' }
    ],
    casesByType: [
      { type: 'Contract Review', count: 5, color: '#064d51' },
      { type: 'Regulatory Compliance', count: 3, color: '#0a7c82' },
      { type: 'Employment Issue', count: 2, color: '#c0f2f3' },
      { type: 'Dispute Resolution', count: 2, color: '#3498db' }
    ],
    
    // Legal Documents
    documentsByStatus: [
      { status: 'Draft', count: 4, color: '#f39c12' },
      { status: 'Under Review', count: 6, color: '#3498db' },
      { status: 'Approved', count: 15, color: '#27ae60' },
      { status: 'Rejected', count: 2, color: '#e74c3c' }
    ],
    
    // Case Resolution
    resolutionTime: {
      labels: ['Contract Review', 'Regulatory Compliance', 'Employment Issue', 'Dispute Resolution'],
      datasets: [
        {
          label: 'Average Days',
          data: [5.3, 10.1, 7.5, 15.2],
          backgroundColor: '#064d51',
        }
      ]
    },
    
    // Monthly case volume
    monthlyCaseVolume: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      datasets: [
        {
          label: 'Legal Cases',
          data: [4, 6, 3, 7, 8],
          borderColor: '#064d51',
          backgroundColor: 'rgba(6, 77, 81, 0.1)',
          tension: 0.4,
        }
      ]
    },
    
    // Upcoming Legal Deadlines
    upcomingDeadlines: [
      { caseTitle: 'Employment Contract Renewal', deadline: '2025-06-15', daysRemaining: 21, priority: 'Medium' },
      { caseTitle: 'Regulatory Compliance Filing', deadline: '2025-06-08', daysRemaining: 14, priority: 'High' },
      { caseTitle: 'Vendor Agreement Review', deadline: '2025-06-02', daysRemaining: 8, priority: 'Medium' },
      { caseTitle: 'IP Rights Documentation', deadline: '2025-05-29', daysRemaining: 4, priority: 'High' },
      { caseTitle: 'Shareholder Agreement Update', deadline: '2025-05-28', daysRemaining: 3, priority: 'Critical' }
    ],
    
    // Recent Case Activity
    recentActivity: [
      { timestamp: '2025-05-25 14:23', action: 'Document Submitted', user: 'Legal Team', details: 'Employment Contract Draft v2' },
      { timestamp: '2025-05-25 13:05', action: 'Case Update', user: 'Sarah Johnson', details: 'Provided additional information for compliance filing' },
      { timestamp: '2025-05-25 11:37', action: 'Case Created', user: 'Mohammed Al-Ali', details: 'New vendor agreement review request' },
      { timestamp: '2025-05-25 10:42', action: 'Document Approved', user: 'Legal Team', details: 'Non-disclosure agreement for Project Alpha' },
      { timestamp: '2025-05-25 09:15', action: 'Case Resolved', user: 'Legal Team', details: 'Employee handbook update completed' }
    ]
  };
};

// Chart configurations
const getLegalDashboardCharts = (data) => {
  return {
    caseStatusChart: {
      labels: data.casesByStatus.map(item => item.status),
      datasets: [
        {
          data: data.casesByStatus.map(item => item.count),
          backgroundColor: data.casesByStatus.map(item => item.color),
          borderWidth: 0,
        }
      ]
    },
    caseTypeChart: {
      labels: data.casesByType.map(item => item.type),
      datasets: [
        {
          data: data.casesByType.map(item => item.count),
          backgroundColor: data.casesByType.map(item => item.color),
          borderWidth: 0,
        }
      ]
    },
    documentStatusChart: {
      labels: data.documentsByStatus.map(item => item.status),
      datasets: [
        {
          data: data.documentsByStatus.map(item => item.count),
          backgroundColor: data.documentsByStatus.map(item => item.color),
          borderWidth: 0,
        }
      ]
    }
  };
};

// Dashboard component
const LegalDashboard = () => {
  const [dashboardData, setDashboardData] = React.useState(null);
  const [charts, setCharts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchLegalDashboardData();
        setDashboardData(data);
        setCharts(getLegalDashboardCharts(data));
      } catch (error) {
        console.error('Error loading legal dashboard data:', error);
        // In production, implement proper error handling
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  if (loading || !dashboardData) {
    return (
      <DashboardContainer>
        <DashboardSelector />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>Loading dashboard data...</Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  return (
    <DashboardContainer>
      <DashboardSelector />
      <Typography variant="h5" sx={{ my: 2, color: '#064d51', fontWeight: 600 }}>
        Legal Support Dashboard
      </Typography>
      
      {/* Top Section - Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{dashboardData.activeCases}</MetricValue>
            <MetricLabel>Active Legal Cases</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">2 more than last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{dashboardData.documentsByStatus.find(d => d.status === 'Under Review')?.count || 0}</MetricValue>
            <MetricLabel>Documents Under Review</MetricLabel>
            <TrendIndicator trend="down">
              <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">1 less than last week</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MetricValue>{dashboardData.upcomingDeadlines.filter(d => d.daysRemaining <= 7).length}</MetricValue>
              <GavelIcon sx={{ color: '#e74c3c', ml: 1 }} />
            </Box>
            <MetricLabel>Urgent Deadlines (7 days)</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">1 more than last week</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{dashboardData.casesByStatus.find(s => s.status === 'Resolved')?.count || 0}</MetricValue>
            <MetricLabel>Resolved Cases (This Month)</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">3 more than last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
      </Grid>
      
      {/* Middle Section - Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardHeader
              title="Monthly Case Volume"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Line
                data={dashboardData.monthlyCaseVolume}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardHeader
              title="Case Status"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Pie
                data={charts.caseStatusChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      },
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Case Types"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Pie
                data={charts.caseTypeChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      },
                    },
                  },
                }}
                height={180}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Resolution Time by Case Type"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Bar
                data={dashboardData.resolutionTime}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Days',
                      },
                    },
                  },
                }}
                height={180}
              />
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
      
      {/* Bottom Section - Tables */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Upcoming Legal Deadlines"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Days Left</TableCell>
                      <TableCell>Priority</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.upcomingDeadlines.map((deadline, index) => (
                      <TableRow key={index}>
                        <TableCell>{deadline.caseTitle}</TableCell>
                        <TableCell>{deadline.deadline}</TableCell>
                        <TableCell>
                          <Chip 
                            label={deadline.daysRemaining} 
                            size="small"
                            sx={{ 
                              backgroundColor: deadline.daysRemaining <= 7 ? '#e74c3c' : 
                                              deadline.daysRemaining <= 14 ? '#f39c12' : '#27ae60',
                              color: 'white',
                              fontWeight: 500,
                              minWidth: '32px'
                            }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={deadline.priority} 
                            size="small"
                            sx={{ 
                              backgroundColor: 
                                deadline.priority === 'Critical' ? '#e74c3c' :
                                deadline.priority === 'High' ? '#f39c12' : '#3498db',
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Recent Case Activity"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentActivity.map((activity, index) => (
                      <TableRow key={index}>
                        <TableCell>{activity.timestamp}</TableCell>
                        <TableCell>
                          <Chip 
                            label={activity.action} 
                            size="small"
                            sx={{ 
                              backgroundColor: 
                                activity.action.includes('Approved') ? '#27ae60' :
                                activity.action.includes('Submitted') ? '#3498db' :
                                activity.action.includes('Created') ? '#f39c12' :
                                activity.action.includes('Resolved') ? '#27ae60' : '#064d51',
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }} 
                          />
                        </TableCell>
                        <TableCell>{activity.user}</TableCell>
                        <TableCell>{activity.details}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </DashboardContainer>
  );
};

export default LegalDashboard;
